import React from 'react';
import { Alert, Card, Col, Row, Space, Statistic, Tag, Typography } from 'antd';
import { formatMoney, formatPercent } from '@/utils/format';
import { useWave6Pricing, type Wave6Ctx } from './EpcEstimateWave6Panels';

const { Text } = Typography;

export type Est3CommercialLeafKey =
  | 'pricing_strategy'
  | 'client_pricing'
  | 'profitability'
  | 'sensitivity'
  | 'bid_reconciliation';

export interface EpcEstimateCommercialStripProps {
  ctx: Wave6Ctx;
  activeLeaf?: Est3CommercialLeafKey;
}

/**
 * EST3 UX2 Wave 7 — Commercial pricing strip (TEC → bid bridge summary).
 */
export const EpcEstimateCommercialStrip: React.FC<EpcEstimateCommercialStripProps> = ({
  ctx,
  activeLeaf,
}) => {
  const { summaryQ, recQ } = useWave6Pricing(ctx);
  const bridge = summaryQ.data?.commercial_bridge;
  const rec = recQ.data;

  return (
    <Card size="small" type="inner">
      <Row gutter={16}>
        <Col xs={24} sm={6}>
          <Statistic
            title="Total Estimated Cost"
            value={Number(summaryQ.data?.total_estimated_cost) || 0}
            formatter={(v) => formatMoney(v, { display: 'plain', maximumFractionDigits: 0 })}
          />
        </Col>
        <Col xs={24} sm={6}>
          <Statistic
            title="Final Bid Price"
            value={bridge?.final_bid_price != null ? Number(bridge.final_bid_price) : 0}
            formatter={(v) =>
              bridge?.final_bid_price != null
                ? formatMoney(v, { display: 'plain', maximumFractionDigits: 0 })
                : '—'
            }
          />
        </Col>
        <Col xs={24} sm={6}>
          <Statistic
            title="Profit / Margin"
            value={bridge?.profit != null ? Number(bridge.profit) : 0}
            formatter={() =>
              bridge?.profit != null
                ? `${formatMoney(bridge.profit, { display: 'plain', maximumFractionDigits: 0 })} · ${formatPercent(Number(bridge.margin_actual) || 0, { maximumFractionDigits: 1 })}`
                : '—'
            }
          />
        </Col>
        <Col xs={24} sm={6}>
          <Space direction="vertical" size={4}>
            <Text type="secondary">Status</Text>
            <Space wrap>
              <Tag color={summaryQ.data?.commercial_pricing === 'READY' ? 'green' : 'orange'}>
                Pricing {summaryQ.data?.commercial_pricing || 'NOT STARTED'}
              </Tag>
              {activeLeaf === 'client_pricing' || activeLeaf === 'bid_reconciliation' ? (
                <Tag color={rec?.reconciled ? 'green' : 'warning'}>
                  {rec?.reconciled ? 'Reconciled' : 'Not reconciled'}
                </Tag>
              ) : null}
            </Space>
          </Space>
        </Col>
      </Row>
      {summaryQ.data?.stale ? (
        <Alert
          style={{ marginTop: 12 }}
          type="warning"
          showIcon
          message="Cost changed after pricing — review commercial strategy and client lines."
        />
      ) : null}
    </Card>
  );
};

export interface EpcEstimateCommercialLeafProps {
  ctx: Wave6Ctx;
  leaf: Est3CommercialLeafKey;
  children: React.ReactNode;
}

/**
 * EST3 UX2 Wave 7 — Commercial leaf wrapper (pricing strip + panel).
 */
export const EpcEstimateCommercialLeaf: React.FC<EpcEstimateCommercialLeafProps> = ({
  ctx,
  leaf,
  children,
}) => (
  <Space direction="vertical" style={{ width: '100%' }} size="middle">
    <EpcEstimateCommercialStrip ctx={ctx} activeLeaf={leaf} />
    {children}
  </Space>
);
